import { useState, useEffect } from 'react';
import './DateRangeCalendar.css';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export default function DateRangeCalendar({ startDate, endDate, onDateSelect, shiftMarkers, onBadgeClick }) {
  const initialDate = startDate ? new Date(startDate) : new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

  useEffect(() => {
    if (startDate) {
      const d = new Date(startDate)
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1))
    }
  }, [startDate]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const handlePrevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Получить первую букву имени
  const getInitial = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    return parts[0].charAt(0).toUpperCase()
  }

  // Короткое имя (первое слово)
  const getShortName = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    const first = parts[0]
    return first.length > 6 ? first.slice(0, 5) + '.' : first
  }

  const renderShiftBadges = (dateStr) => {
    if (!shiftMarkers || !shiftMarkers[dateStr]) return null
    const markers = shiftMarkers[dateStr]
    if (!markers || markers.length === 0) return null
    return (
      <div className="cal-shift-badges">
        {markers.map((m, i) => {
          const cls = m.filled === true ? 'cal-badge ok' : m.filled === false ? 'cal-badge miss' : 'cal-badge future'
          const isClickable = m.filled === false && onBadgeClick
          const typeLabels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
          const missingStr = m.missingTypes?.length ? `\nНет: ${m.missingTypes.map(t => typeLabels[t] || t).join(', ')}` : ''
          const title = m.filled === false 
            ? `${m.name} — нажмите чтобы внести${missingStr}` 
            : m.filled === true 
              ? `${m.name} — всё внесено` 
              : m.name
          return (
            <span 
              key={i} 
              className={cls + (isClickable ? ' clickable' : '')} 
              title={title}
              onClick={isClickable ? (e) => { e.stopPropagation(); onBadgeClick(dateStr, m.id) } : undefined}
            >
              {getShortName(m.name)}
            </span>
          )
        })}
      </div>
    )
  }

  const fmtDate = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const todayStr = (() => {
    const td = new Date()
    return fmtDate(td.getFullYear(), td.getMonth(), td.getDate())
  })()

  const hasMarkers = shiftMarkers && Object.keys(shiftMarkers).length > 0

  const renderDays = () => {
    const days = [];
    
    // Предыдущий месяц
    const prevMonthDate = new Date(year, month - 1, 1);
    const prevYear = prevMonthDate.getFullYear();
    const prevMonth = prevMonthDate.getMonth();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    for (let i = startDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const dateStr = fmtDate(prevYear, prevMonth, d);
      days.push(renderDayCell(dateStr, d, true));
    }
    
    // Текущий месяц
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = fmtDate(year, month, d);
      days.push(renderDayCell(dateStr, d, false));
    }

    // Следующий месяц
    const nextMonthDate = new Date(year, month + 1, 1);
    const nextYear = nextMonthDate.getFullYear();
    const nextMonth = nextMonthDate.getMonth();
    const totalFilled = days.length;
    const remainingDays = 42 - totalFilled;

    for (let d = 1; d <= remainingDays; d++) {
      const dateStr = fmtDate(nextYear, nextMonth, d);
      days.push(renderDayCell(dateStr, d, true));
    }

    return days;
  };

  const renderDayCell = (dateStr, dayNum, isInactive) => {
    const isStart = startDate === dateStr;
    const isEnd = endDate === dateStr;
    const isSelected = isStart || isEnd;
    const isToday = todayStr === dateStr;
    const isBetween = startDate && endDate && dateStr > startDate && dateStr < endDate;

    let wrapperClass = 'calendar-day-wrapper' + (hasMarkers ? ' has-markers' : '');
    if (isInactive) wrapperClass += ' inactive';
    if (isBetween) wrapperClass += ' in-range';
    if (isStart && endDate) wrapperClass += ' range-start';
    if (isEnd && startDate) wrapperClass += ' range-end';
    if (isStart && isEnd) wrapperClass += ' range-start range-end';

    let dayClass = 'calendar-day';
    if (isInactive) dayClass += ' inactive-day';
    if (isSelected) dayClass += ' selected';
    else if (isToday) dayClass += ' today';

    return (
      <div key={dateStr} className={wrapperClass} onClick={() => onDateSelect(dateStr)}>
        <div className={dayClass}>{dayNum}</div>
        {hasMarkers && renderShiftBadges(dateStr)}
      </div>
    );
  };

  return (
    <div className={`custom-calendar ${hasMarkers ? 'with-shifts' : ''}`}>
      <div className="calendar-header">
        <button type="button" className="calendar-nav-btn" onClick={handlePrevMonth}>‹</button>
        <div className="calendar-month-year">{MONTH_NAMES[month]} {year}</div>
        <button type="button" className="calendar-nav-btn" onClick={handleNextMonth}>›</button>
      </div>
      <div className="calendar-weekdays">
        {WEEKDAYS.map(wd => <div key={wd} className="calendar-weekday">{wd}</div>)}
      </div>
      <div className="calendar-grid">
        {renderDays()}
      </div>
      {/* Подсказка статусов */}
      {hasMarkers && (
        <div className="cal-status-hint">
          <span className="cal-badge ok" style={{ fontSize: '10px', padding: '1px 5px' }}>Имя</span> — операции есть
          <span style={{ margin: '0 6px' }}>|</span>
          <span className="cal-badge miss" style={{ fontSize: '10px', padding: '1px 5px' }}>Имя</span> — нет операций
        </div>
      )}
    </div>
  );
}
